import { FC, useRef, useState, useEffect } from 'react';
import { Checkbox } from 'antd';

import { useAppDispatch, useAppSelector } from 'hooks/redux';
import HalfScreenModal from 'components/HalfScreenModal/HalfScreenModal';
import PlainTextEditor from 'components/PlainTextEditor/PlainTextEditor';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import { createRichMedia } from 'modules/benefitsWellness/slices/legalDisclaimerSlice';
import { SectionName } from 'modules/home/constants';
import {
  getFooterTemplate,
  saveFooterTemplate,
} from 'modules/auth/services/BenguideService';

type LegalDisclaimerEditorProps = {
  isOpenModal: boolean;
  closeModal: Function;
  editorContent: string;
  updateEditorContent: (data: string) => void;
  resetToDefault: () => void;
  isResetEnabled: boolean;
  benguideId?: string;
  defaultContent: string;
  resetToCurrentValue: Function;
  onCreate: Function;
  onTemplateUpdate?: (content: string) => void;
};

const LegalDisclaimerEditor: FC<LegalDisclaimerEditorProps> = (
  props: LegalDisclaimerEditorProps
) => {
  const {
    isOpenModal,
    closeModal,
    editorContent,
    updateEditorContent,
    benguideId,
    defaultContent,
    resetToCurrentValue,
    onCreate,
    onTemplateUpdate,
  } = props;

  const textEditorRef = useRef<any>(null);
  const dispatch = useAppDispatch();

  const organizationId = useAppSelector((state) => state.benguide.data?.orgId);

  const loggedInUserType = useAppSelector(
    (state) => state.benguide.loggedInUserType
  );

  const loggedInUserSubtype = useAppSelector(
    (state) => state.benguide.loggedInUserSubtype
  );

  const isBrokerAdmin =
    loggedInUserSubtype === 'BROKER_ADMIN' ||
    loggedInUserSubtype === 'BROKER_SUPER_ADMIN' ||
    loggedInUserType === 'PLATFORM_ADMIN';

  const [currentContent, setCurrentContent] = useState<string>(editorContent);
  const [saveAsDefault, setSaveAsDefault] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [pendingContent, setPendingContent] = useState<string>('');

  const handleResetToDefault = async () => {
    if (!organizationId) return;

    try {
      const response = await getFooterTemplate(organizationId);
      const templateContent = response?.data || defaultContent;

      // Update the editor with template or fallback to defaultContent
      if (templateContent && textEditorRef.current) {
        textEditorRef.current.changeEditorContent(templateContent);
        handleEditorChange(templateContent);
      }
    } catch (error) {
      console.error('Error fetching default template:', error);
      // On error, fall back to defaultContent
      if (defaultContent && textEditorRef.current) {
        textEditorRef.current.changeEditorContent(defaultContent);
        handleEditorChange(defaultContent);
      }
    }
  };

  const closeOverviewModal = () => {
    resetToCurrentValue();
    if (textEditorRef.current) {
      textEditorRef.current.clearTextEditorContent(editorContent);
    }
    closeModal();
  };

  const handleEditorChange = (data: string) => {
    const sanitizedContent = sanitizeContent(data);
    updateEditorContent(sanitizedContent);
    setCurrentContent(sanitizedContent);
  };

  const saveFooterContent = async (content: string) => {
    if (!benguideId) return;

    try {
      await new Promise((resolve) => {
        dispatch(
          createRichMedia(benguideId, SectionName.FOOTER_TEXT, content, () => {
            if (onCreate) {
              onCreate(content);
            }
            resolve(null);
          })
        );
      });
    } catch (error) {
      console.error('Error saving footer content:', error);
      throw error;
    }
  };

  const saveAsDefaultTemplate = async (content: string) => {
    if (!organizationId) {
      console.error('Organization ID is required to save default template');
      return;
    }

    try {
      if (!benguideId) return;
      await saveFooterTemplate(organizationId, benguideId, content);

      // Notify parent component about template update
      if (onTemplateUpdate) {
        onTemplateUpdate(content);
      }
    } catch (error) {
      console.error('Error saving default template:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    const currentContent =
      textEditorRef?.current?.getEditorRef()?.current?.value || editorContent;

    // If "Save as Default" is checked and user is broker admin, show confirmation
    if (saveAsDefault && isBrokerAdmin) {
      setPendingContent(currentContent);
      setShowConfirmation(true);
      return;
    }

    await saveFooterContent(currentContent);
    closeModal();
  };

  const handleConfirmSaveAsDefault = async () => {
    setShowConfirmation(false);

    try {
      // Save footer content first
      await saveFooterContent(pendingContent);

      // Then save as default template
      await saveAsDefaultTemplate(pendingContent);

      closeModal();
    } catch (error) {
      console.error('Error in confirmation handler:', error);
      // Handle error appropriately (show error message to user)
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setPendingContent('');
  };

  // Add sanitization function
  const sanitizeContent = (content: string) => {
    return content
      .replace(/&#64;/g, '@')
      .replace(/&#43;/g, '+')
      .replace(/&#34;/g, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  };

  useEffect(() => {
    if (isOpenModal) {
      const sanitizedContent = sanitizeContent(editorContent);
      setCurrentContent(sanitizedContent);

      if (textEditorRef.current) {
        textEditorRef.current.changeEditorContent(sanitizedContent);
      }
    }
  }, [editorContent, isOpenModal]);

  return (
    <>
      <HalfScreenModal
        width={900}
        title="Edit Footer"
        visible={isOpenModal}
        onCancel={closeOverviewModal}
        onSubmit={handleSubmit}
        confirmModalShow
        isResetEnabled={!!defaultContent}
        onReset={handleResetToDefault}
        resetText="Reset to Default Template"
        customLeftContent={
          isBrokerAdmin ? (
            <Checkbox
              checked={saveAsDefault}
              onChange={(e) => setSaveAsDefault(e.target.checked)}
            >
              Save as default template
            </Checkbox>
          ) : null
        }
        increaseResetSpacing
        afterClose={() => {
          textEditorRef?.current?.resetUndo();
          textEditorRef?.current?.clearSelection();
          setSaveAsDefault(false);
          setPendingContent('');
        }}
      >
        <PlainTextEditor
          limit={220}
          initialValue={sanitizeContent(editorContent)}
          onChange={handleEditorChange}
          defaultText={sanitizeContent(currentContent)}
          ref={textEditorRef}
          height={280}
        />
      </HalfScreenModal>

      <ConfirmationDialog
        visible={showConfirmation}
        title="Save As Default Template"
        okText="Yes - Save as Default Template"
        cancelText="Cancel"
        onCancel={handleCancelConfirmation}
        onOk={handleConfirmSaveAsDefault}
        width={464}
        flipFooterOrder={true}
      >
        Are you sure you want to set this footer as the default template? This
        will be used as the starting point for all future microsite footer
        sections. You can still customize each one later in the Footer Editor.
      </ConfirmationDialog>
    </>
  );
};

export default LegalDisclaimerEditor;
